import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { Send, Bell, Users as UsersIcon } from "lucide-react";

const TIERS = ["all", "free", "premium", "platinum"];

export default function PushNotificationsScreen() {
  const [form, setForm] = useState({ title: "", body: "", target_tier: "all", target_country: "", deep_link: "" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadHistory = async () => {
    try {
      const { data } = await adminApi.get("/campaigns?channel=push");
      setHistory(data.campaigns || []);
    } catch {}
  };
  useEffect(() => { loadHistory(); }, []);

  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form };
      if (!body.target_country) delete body.target_country;
      if (!body.deep_link) delete body.deep_link;
      const { data } = await adminApi.post("/notifications/push", body);
      toast.success(`Delivered to ${data.delivered_count} users`);
      setForm({ title: "", body: "", target_tier: "all", target_country: "", deep_link: "" });
      loadHistory();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-shadow-green mb-1">Push Notifications</h1>
      <p className="text-muted-soft text-sm mb-6">Send in-app notifications to segments.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <form onSubmit={send} className="glass-card-strong p-6" data-testid="push-form">
          <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">New campaign</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-soft">Title (max 50)</label>
              <input className="glass-input mt-1" maxLength={50} required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="push-title" />
            </div>
            <div>
              <label className="text-xs text-muted-soft">Body (max 150)</label>
              <textarea className="glass-input mt-1" maxLength={150} required rows={3} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="push-body" />
              <p className="text-[10px] text-muted-soft mt-1 text-right">{form.body.length}/150</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-soft">Target tier</label>
                <select className="glass-input mt-1" value={form.target_tier} onChange={(e) => setForm({ ...form, target_tier: e.target.value })} data-testid="push-target-tier">
                  {TIERS.map((t) => <option key={t} value={t} style={{ background: "#0A0A0A" }}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-soft">Country (optional)</label>
                <input className="glass-input mt-1" placeholder="e.g. India" value={form.target_country} onChange={(e) => setForm({ ...form, target_country: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-soft">Deep link (optional)</label>
              <input className="glass-input mt-1" placeholder="/identify" value={form.deep_link} onChange={(e) => setForm({ ...form, deep_link: e.target.value })} />
            </div>
          </div>

          {/* Preview */}
          <div className="mt-5 mb-4 rounded-xl p-4" style={{ background: "rgba(76,175,80,0.05)", border: "1px solid rgba(76,175,80,0.18)" }}>
            <p className="text-[10px] uppercase tracking-wider text-muted-soft mb-2">Preview</p>
            <div className="flex items-start gap-3">
              <Bell size={16} className="text-[#4CAF50] mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold">{form.title || "Title preview"}</p>
                <p className="text-xs text-soft mt-0.5">{form.body || "Body preview…"}</p>
              </div>
            </div>
          </div>

          <button type="submit" disabled={busy} className="btn-primary w-full inline-flex items-center justify-center gap-2" data-testid="push-send">
            <Send size={14} /> {busy ? "Sending…" : "Send now"}
          </button>
        </form>

        <div className="glass-card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <UsersIcon size={14} className="text-[#4CAF50]" />
            <p className="text-sm font-semibold">Recent push campaigns</p>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {history.length === 0 ? (
              <p className="p-8 text-center text-soft text-sm">No campaigns yet.</p>
            ) : history.map((c) => (
              <div key={c.campaign_id} className="px-4 py-3 border-b border-white/3 last:border-b-0" data-testid={`push-history-${c.campaign_id}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold truncate">{c.title}</p>
                    <p className="text-[11px] text-muted-soft mt-0.5">{c.body}</p>
                  </div>
                  <span className="glass-pill text-[10px]">{c.target_tier}</span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-muted-soft">
                  <span>📤 {c.delivered_count} delivered</span>
                  <span>·</span>
                  <span>{new Date(c.created_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
